import React, { useEffect, useMemo, useState } from "react";
import { Alert, Input, Radio, Select, Spin, Table, Tag, Typography } from "antd";
import { Link } from "react-router-dom";

import { getExampleUsage } from "./prospectService";
import styles from "./style.module.scss";

// Pivony's own view of the example demos (app.pivony.com/demos/<slug>): how often each example and
// each labelled link was opened, asked and clicked. Staff only (@pivony.com); the API refuses others.

export const DEMO_BASE = "https://app.pivony.com/demos";
const ASCII = { ç: "c", ğ: "g", ı: "i", ö: "o", ş: "s", ü: "u", â: "a", î: "i", û: "u", İ: "i" };

/** Same rule as the API (prospect_examples.clean_ref): "Gelinlik Dünyası" -> "gelinlik-dunyasi". */
export function cleanRef(raw) {
  const label = String(raw || "")
    .replace(/[çğıöşüâîûİ]/gi, (ch) => ASCII[ch] || ASCII[ch.toLowerCase()] || ch)
    .trim()
    .toLowerCase();
  return label.replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48).replace(/-+$/, "");
}

export function exampleLink(slug, label) {
  const ref = cleanRef(label);
  return `${DEMO_BASE}/${slug}${ref ? `?ref=${ref}` : ""}`;
}

const COUNTS = [
  { key: "view", title: "Opened" },
  { key: "question", title: "Questions" },
  { key: "limit", title: "Hit the limit" },
  { key: "signup", title: "Sign-up clicks" },
  { key: "call", title: "Book-a-call clicks" },
];

const when = (value) => (value ? new Date(value).toLocaleString() : "");

const ExampleLinks = () => {
  const [days, setDays] = useState(30);
  const [state, setState] = useState({ loading: true });
  const [slug, setSlug] = useState("atelier-blanc-tr");
  const [label, setLabel] = useState("");

  useEffect(() => {
    let stop = false;
    setState((prev) => ({ ...prev, loading: true }));
    getExampleUsage(days)
      .then((data) => !stop && setState({ loading: false, data }))
      .catch((error) => !stop && setState({ loading: false, error: error.code || "unavailable" }));
    return () => {
      stop = true;
    };
  }, [days]);

  const data = state.data;
  const names = useMemo(() => Object.fromEntries((data?.examples || []).map((ex) => [ex.slug, ex])), [data]);
  const options = useMemo(
    () =>
      ["tr", "en"].map((lang) => ({
        label: lang === "tr" ? "Turkish" : "English",
        options: (data?.examples || [])
          .filter((ex) => ex.lang === lang)
          .map((ex) => ({ value: ex.slug, label: `${ex.sector_label} · ${ex.name}` })),
      })),
    [data]
  );

  const exampleCell = (value) => {
    const ex = names[value] || {};
    return (
      <span>
        {ex.name || value} {ex.lang ? <Tag>{ex.lang.toUpperCase()}</Tag> : null}
      </span>
    );
  };
  const countColumns = COUNTS.map(({ key, title }) => ({
    title,
    dataIndex: key,
    key,
    align: "right",
    sorter: (a, b) => a[key] - b[key],
  }));

  if (state.error === "forbidden") {
    return (
      <div className={styles.listPage}>
        <Alert type="info" showIcon message="Only Pivony team accounts (@pivony.com) can see example link stats." />
      </div>
    );
  }

  return (
    <div className={styles.listPage}>
      <div className={styles.pageHeader}>
        <div>
          <h1>Example demo links</h1>
          <p>
            Who opens the example demos you send, and what they do there. Add a label to a link to see that
            business on its own. Read what visitors asked on the{" "}
            <Link to="/console/ops/demo-conversations">demo conversations</Link> page (kept 30 days).
          </p>
        </div>
        <Radio.Group value={days} onChange={(e) => setDays(e.target.value)} optionType="button">
          <Radio.Button value={7}>7 days</Radio.Button>
          <Radio.Button value={30}>30 days</Radio.Button>
          <Radio.Button value={90}>90 days</Radio.Button>
        </Radio.Group>
      </div>

      <section className={styles.exampleLinkMaker}>
        <h3>Make a link to send</h3>
        <div className={styles.exampleLinkRow}>
          <Select
            value={slug}
            onChange={setSlug}
            options={options}
            style={{ minWidth: 300 }}
            aria-label="Example"
          />
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Business name, e.g. Gelinlik Dünyası"
            aria-label="Link label"
            style={{ maxWidth: 320 }}
          />
        </div>
        <Typography.Paragraph copyable className={styles.exampleLinkOut}>
          {exampleLink(slug, label)}
        </Typography.Paragraph>
      </section>

      {state.error ? <Alert type="warning" showIcon message="Stats are unavailable right now. Try again shortly." /> : null}
      <Spin spinning={state.loading}>
        <div className={styles.exampleTotals}>
          {COUNTS.map(({ key, title }) => (
            <div key={key} className={styles.exampleTotal}>
              <span>{title}</span>
              <b>{data?.totals?.[key] ?? 0}</b>
            </div>
          ))}
        </div>

        <h3 className={styles.exampleSection}>By link label</h3>
        <Table
          size="small"
          rowKey={(row) => `${row.ref}:${row.slug}`}
          dataSource={data?.refs || []}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          locale={{ emptyText: "No labelled links opened yet." }}
          columns={[
            { title: "Label", dataIndex: "ref", key: "ref" },
            { title: "Example", dataIndex: "slug", key: "slug", render: exampleCell },
            ...countColumns,
            { title: "First opened", dataIndex: "first_at", key: "first_at", render: when },
            { title: "Last activity", dataIndex: "last_at", key: "last_at", render: when },
          ]}
        />

        <h3 className={styles.exampleSection}>By example</h3>
        <Table
          size="small"
          rowKey="slug"
          dataSource={data?.examples || []}
          pagination={false}
          columns={[
            { title: "Example", dataIndex: "slug", key: "slug", render: exampleCell },
            { title: "Industry", dataIndex: "sector_label", key: "sector_label" },
            ...countColumns,
          ]}
        />
      </Spin>
    </div>
  );
};

export default ExampleLinks;
